/**
 * Core query-param parsing for the widget shell (P3-4 / MR-32).
 *
 * These helpers are extracted verbatim from app.vue's `extractQueryParams`,
 * `processQueryParams`, and `createStationInformation`. The public embedding
 * contract is BYTE-FROZEN, so the extraction preserves the exact Vue1 output
 * for every input. Pulling them out as pure functions lets the fiddly
 * edge-case behavior be unit-tested without mounting a Vue component.
 *
 * The ONE deliberate deviation from Vue1: `parseQueryString` catches a
 * URIError from malformed %-encoding and falls back to the raw value, where
 * Vue1 let the error propagate out of the boot and crash the widget (hunter #2).
 */

/**
 * Parse a raw query string (the part after `?`, no leading `?`) into a flat
 * params object.
 *
 * Byte-identical to Vue1's hand parser:
 *   - `const [k, v] = s.split('=')` — for `k=a=b` this keeps only the segment
 *     between the 1st and 2nd `=` and DROPS the tail (v === 'a'), and for a
 *     bare key (`?flag`) v is undefined → `decodeURIComponent(undefined)` is
 *     the string 'undefined'. Both are preserved on purpose.
 *   - `Object.assign({}, ...)` — duplicate keys are LAST-wins.
 *   - `decodeURIComponent` does NOT turn `+` into a space (a URLSearchParams
 *     rewrite would, silently changing the contract — so we keep the hand
 *     parser).
 *
 * @param {string} qs raw query string (no leading `?`)
 * @returns {Object<string,string>} parsed params
 */
export const parseQueryString = (qs) =>
  Object.assign(
    {},
    ...qs.split('&').map((s) => {
      const [k, v] = s.split('=');
      let decoded;
      try {
        decoded = decodeURIComponent(v);
      } catch (e) {
        // Malformed %-encoding (e.g. an unencoded `100%`). Vue1 threw a
        // URIError out of the boot here; fall back to the raw value instead
        // of crashing the boot (hunter #2).
        decoded = v;
      }
      return { [k]: decoded };
    })
  );

/**
 * Derive the core shell config from an already-parsed params object.
 *
 * Every field mirrors app.vue exactly — in particular `Number(version) || 1`
 * (absent/0/NaN all default to 1; do NOT "fix" to 4), `smc` falling back to
 * `zone`, and `useM4a` being true only for the literal string 'true'.
 *
 * P6-5 (MR-54) adds the FORCE_V4 clamp: when `options.forceV4` is `true`, a
 * legacy requested version (anything but 4) resolves to `version: 4` so the
 * shell renders the v4 player for every embed. It is OFF unless `forceV4 === true`
 * (the exact boolean — a truthy string/number does NOT enable it), so the default
 * single-argument call preserves the byte-frozen contract. `requestedVersion`
 * always carries the ORIGINAL pre-clamp version (P6-17 reads it via telemetry).
 *
 * @param {Object<string,string>} queryParams parsed query params
 * @param {{forceV4?: boolean}} [options] `forceV4` clamps legacy → v4 (default OFF)
 * @returns {Object} core config (adds `requestedVersion`, the pre-clamp version)
 */
export const parseCoreConfig = (queryParams, options) => {
  const forceV4 = !!options && options.forceV4 === true;
  const zone = queryParams.zone;
  const next = queryParams.next || 'none';
  // The version the embed actually requested, pre-clamp — `Number(version) || 1`
  // verbatim (absent/0/NaN → 1; do NOT "fix" to 4).
  const requestedVersion = Number(queryParams.version) || 1;
  return {
    // FORCE_V4 clamp: legacy (non-4) → 4 only when the flag is ON; otherwise the
    // requested version passes through unchanged (frozen contract).
    version: forceV4 && requestedVersion !== 4 ? 4 : requestedVersion,
    requestedVersion,
    zone,
    smc: queryParams.smc || zone,
    limit: parseInt(queryParams.limit || '10000', 10),
    page: parseInt(queryParams.page || '1', 10),
    perPage: queryParams.perPage,
    next,
    theme: queryParams.theme || 'light',
    mods: (queryParams.mods || '').split(','),
    shared: parseInt(queryParams.futurishared || '0', 10),
    singlePlaylistId: queryParams.id,
    useM4a: queryParams.use_m4a === 'true',
    displayPlayerInline: next === 'none' || next === 'continue',
    dropToLive: next === 'continue',
  };
};

/**
 * Build the per-station list from the `station` param.
 *
 * Defaults to `wphm` (carrying the shell `zone`) when absent; a single station
 * carries the shell zone; a `station:zone,station:zone` list is split into
 * per-station entries (each with its own zone). Mirrors
 * `createStationInformation` exactly.
 *
 * @param {?Object<string,string>} queryParams parsed query params
 * @param {string} zone the shell zone (fallback for single-station embeds)
 * @returns {Array<{station:string, zone:string}>}
 */
export const parseStations = (queryParams, zone) => {
  const stationlist =
    queryParams && queryParams.station ? queryParams.station : 'wphm';
  if (stationlist.indexOf(',') < 0) {
    return [{ station: stationlist, zone }];
  }
  return stationlist.split(',').map((stationZone) => {
    const [station, stnzone] = stationZone.split(':');
    return { station, zone: stnzone };
  });
};
